import { generatePath, Link } from 'react-router-dom';
import { useNavigateWithSearch } from '@/hooks/useNavigateWithSearch';
import ListPane from '@/views/list/ListPane';
import { useGetListsQuery } from '@/features/customListSlice';
import { DetailViewHeader } from '@/views/list/detail/DetailViewHeader';
import {
  Box,
  Flex,
  Loader,
  Stack,
  Anchor,
  Text,
  Tooltip,
  Button,
  Title,
} from '@mantine/core';
import { Icon } from '@/components';
import { ExternalLink, List, Upload } from 'react-feather';

import classes from './CustomList.module.css';
import { formatDateRelative } from '@/helpers/date';
import { useCallback } from 'react';
import { useAppDispatch } from '@/app/hooks';
import { clearItem, setActiveGroupName } from '@/features/analysisMapSlice';
import { LISTS_DETAIL_PATH } from '@/app/constants';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';
import slugify from 'slugify';
import { useDisclosure } from '@mantine/hooks';
import { useAppFlags } from '@/hooks/useAppFlags';
import { CreateProjectModal } from './CreateProjectModal';
import { CreateProjectFromCsvModal } from './CreateProjectFromCsvModal';
import { HELP_ARTICLES } from '@/helpers/helpArticles';

export default function CustomListOverviewPane() {
  const {
    data = [],
    isError,
    isLoading,
    isFetching,
  } = useGetListsQuery({
    sortOrder: 'desc',
    sortBy: 'updated_at',
  });
  const dispatch = useAppDispatch();
  const navigateWithSearch = useNavigateWithSearch();
  const intl = useClientTerminology();
  const { flagUploadProjectFromCsv } = useAppFlags();

  // Create project modal state
  const [
    createProjectModalOpened,
    { open: openCreateProjectModal, close: closeCreateProjectModal },
  ] = useDisclosure(false);

  // CSV upload modal state
  const [
    csvUploadModalOpened,
    { open: openCsvUploadModal, close: closeCsvUploadModal },
  ] = useDisclosure(false);

  /** This is a stopgap to get back into "legacy" (non-URL routed) mode */
  const goUp = useCallback(() => {
    dispatch(setActiveGroupName(null));
    dispatch(clearItem());
    navigateWithSearch('/');
  }, [dispatch, navigateWithSearch]);

  // Handle create project button clicks
  const handleCreateProjectClick = useCallback(() => {
    TagManager.dataLayer({
      dataLayer: {
        event: GoogleEvents.projects.creation.clickedCreateProjectFromOverview,
      },
    });

    openCreateProjectModal();
  }, [openCreateProjectModal]);

  // Handle CSV upload button clicks
  const handleCsvUploadClick = useCallback(() => {
    TagManager.dataLayer({
      dataLayer: {
        event: GoogleEvents.projects.creation.clickedCreateProjectFromOverview,
        params: {
          method: 'csv',
        },
      },
    });

    openCsvUploadModal();
  }, [openCsvUploadModal]);

  return (
    <ListPane
      className={`${classes.root} ${classes.listViewMode}`}
      listWrapperStyles={{
        overflow: 'visible',
      }}
      isLoaded={!isLoading && !isFetching}
    >
      <Box className={classes.header} px="md">
        <DetailViewHeader
          heading={intl.formatMessage({ id: 'customList.allLists' })}
          onBack={goUp}
        />
      </Box>
      {isLoading && (
        <Flex justify="center" mt="md">
          <Loader size="sm" />
        </Flex>
      )}
      {isError && (
        <Flex justify="center">
          <span>
            {intl.formatMessage({ id: 'customList.errorLoadingLists' })}
          </span>
        </Flex>
      )}
      {!isLoading && !isError && data.length === 0 && (
        <Stack my="md" align="start" maw="375px" p="md">
          <Title order={2}>
            {intl.formatMessage({ id: 'customList.overviewZeroStateTitle' })}
          </Title>
          <Text c="dimmed" w="85%">
            {intl.formatMessage({
              id: 'customList.getStarted',
            })}
          </Text>
          <Button
            component="a"
            px={0}
            target="_blank"
            href={HELP_ARTICLES.projects}
            variant="transparent"
            style={{ textDecoration: 'underline' }}
            leftSection={<ExternalLink />}
            onClick={() => {
              TagManager.dataLayer({
                dataLayer: {
                  event:
                    GoogleEvents.projects.creation
                      .clickedCreateProjectFromOverview,
                },
              });
            }}
          >
            {intl.formatMessage({ id: 'action.learnMore' })}
          </Button>

          <Flex gap="sm" wrap="wrap">
            <Button
              className={classes.createButton}
              radius="md"
              onClick={handleCreateProjectClick}
              data-testid="custom-list-create-button"
            >
              {intl.formatMessage({ id: 'customList.createList' })}
            </Button>

            {flagUploadProjectFromCsv && (
              <Button
                variant="outline"
                radius="md"
                onClick={handleCsvUploadClick}
                data-testid="csv-upload-create-button"
                leftSection={<Icon Component={Upload} size={16} />}
              >
                {intl.formatMessage({ id: 'customList.uploadCsv' })}
              </Button>
            )}
          </Flex>
        </Stack>
      )}
      <div
        className={classes.scrollContainer}
        data-testid="custom-list-scroll-area"
      >
        {data.map((list) => (
          <Anchor
            component={Link}
            key={list.id}
            to={generatePath(LISTS_DETAIL_PATH, { id: list.id })}
            className={classes.anchor}
            data-testid={`custom-list-link-${slugify(list.name ?? '')}`}
          >
            <Flex gap="sm" align="center" w="100%">
              <Box className={classes.icon}>
                <Icon Component={List} size={24} />
              </Box>
              <Stack gap="2px" className={classes.textContainer}>
                <Tooltip label={list.name} withArrow openDelay={500}>
                  <Text truncate data-testid="custom-list-name">
                    {list.name}
                  </Text>
                </Tooltip>
                {list.updatedAt ? (
                  <Text size="sm" className={classes.meta}>
                    {intl.formatMessage(
                      {
                        id: 'meta.lastEditedDate',
                      },
                      { date: formatDateRelative(list.updatedAt) }
                    )}
                  </Text>
                ) : null}
              </Stack>
            </Flex>
          </Anchor>
        ))}
      </div>

      {data.length > 0 && (
        <Flex direction="column" gap="sm" mx="md" mb="md">
          <Button
            radius="md"
            className={classes.createButton}
            onClick={handleCreateProjectClick}
            data-testid="custom-list-create-button"
          >
            {intl.formatMessage({ id: 'customList.createList' })}
          </Button>

          {flagUploadProjectFromCsv && (
            <Button
              variant="outline"
              radius="md"
              onClick={handleCsvUploadClick}
              data-testid="csv-upload-create-button"
              leftSection={<Icon Component={Upload} size={16} />}
            >
              {intl.formatMessage({ id: 'customList.uploadCsv' })}
            </Button>
          )}
        </Flex>
      )}

      <CreateProjectModal
        opened={createProjectModalOpened}
        onClose={closeCreateProjectModal}
      />

      {flagUploadProjectFromCsv && (
        <CreateProjectFromCsvModal
          opened={csvUploadModalOpened}
          onClose={closeCsvUploadModal}
        />
      )}
    </ListPane>
  );
}
